import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StatusBar } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useRoute } from '@react-navigation/native';

import { Scroll, Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import {
  LevelDetailHeader,
  LevelDetailPanel,
  LevelDetailPinList,
} from '@/features/guardian-player/components/level-detail';
import { useGuardianPlayerLevelDetail } from '@/features/guardian-player/hooks';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import type { GuardianStackScreenProps } from '@/navigations/types';
import { colors, guardianColors } from '@/theme/colors';

type LevelDetailRoute = GuardianStackScreenProps<'GuardianPlayerLevelDetail'>['route'];

export function GuardianPlayerLevelDetailScreen() {
  const route = useRoute<LevelDetailRoute>();
  const navigation = useGuardianStackNavigation();
  const styles = useStyles();
  const { isTablet } = useScaling();
  const { player, level } = route.params;
  const { data } = useGuardianPlayerLevelDetail(player, level);
  const [selectedPinId, setSelectedPinId] = useState(data.selectedPinId);

  const selectedPin = useMemo(
    () => data.pins.find(pin => pin.sourceIndex === selectedPinId) ?? data.pins[0],
    [data.pins, selectedPinId],
  );

  useEffect(() => {
    setSelectedPinId(data.selectedPinId);
  }, [data.selectedPinId]);

  const handleSelectPin = useCallback(
    (pinId: number) => {
      const pin = data.pins.find(item => item.sourceIndex === pinId);
      if (!pin) return;

      if (isTablet) {
        setSelectedPinId(pinId);
        return;
      }

      navigation.navigate('GuardianPlayerSkillDetail', { pin });
    },
    [data.pins, isTablet, navigation],
  );

  const handleBackPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    navigation.navigate('GuardianDashboard');
  };

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      <StatusBar barStyle="dark-content" backgroundColor={colors['0']} />
      <LevelDetailHeader
        level={data.level}
        progressPercent={data.progressPercent}
        statusLabel={data.levelStatusLabel}
        onBackPress={handleBackPress}
      />

      {isTablet ? (
        <Stack flex={1} direction="row">
          {data.pins.length > 0 && selectedPin ? (
            <>
              <Scroll
                style={styles.tabletListScroll}
                contentContainerStyle={styles.tabletListContent}
                showsVerticalScrollIndicator={false}>
                <LevelDetailPinList
                  tablet
                  pins={data.pins}
                  selectedPinId={selectedPin.sourceIndex}
                  onSelectPin={handleSelectPin}
                />
              </Scroll>
              <Stack style={styles.tabletDivider} />
              <Scroll
                style={styles.tabletPanelScroll}
                contentContainerStyle={styles.tabletPanelContent}
                showsVerticalScrollIndicator={false}>
                <LevelDetailPanel pin={selectedPin} insideScroll noDivider />
              </Scroll>
            </>
          ) : (
            <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.emptyText}>
              {STRINGS.guardianPlayer.levelDetail.emptyProgress}
            </Text>
          )}
        </Stack>
      ) : (
        <LinearGradient
          colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
          locations={[0.08, 0.47, 0.9]}
          useAngle
          angle={210.65}
          style={styles.mobileGradient}>
          <Scroll
            style={styles.mobileScroll}
            contentContainerStyle={styles.mobileContent}
            showsVerticalScrollIndicator={false}>
            {data.pins.length > 0 && selectedPin ? (
              <LevelDetailPinList
                pins={data.pins}
                selectedPinId={selectedPin.sourceIndex}
                onSelectPin={handleSelectPin}
              />
            ) : (
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                style={styles.emptyText}>
                {STRINGS.guardianPlayer.levelDetail.emptyProgress}
              </Text>
            )}
          </Scroll>
        </LinearGradient>
      )}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  emptyText: {
    color: guardianColors['6'],
    paddingHorizontal: scale(20),
    paddingVertical: scale(16),
  },
  tabletListScroll: {
    width: scale(390),
    flexGrow: 0,
    backgroundColor: guardianColors['1'],
  },
  tabletListContent: {
    paddingTop: scale(20),
    paddingLeft: scale(20),
    paddingRight: scale(32),
  },
  tabletPanelScroll: {
    flex: 1,
  },
  tabletDivider: {
    width: 1,
    backgroundColor: guardianColors['3'],
  },
  tabletPanelContent: {
    flexGrow: 1,
    paddingBottom: scale(24),
  },
  mobileGradient: {
    flex: 1,
  },
  mobileScroll: {
    flex: 1,
  },
  mobileContent: {
    paddingHorizontal: scale(20),
    paddingTop: scale(12),
    paddingBottom: scale(40),
  },
}));
